import React from 'react';
import PropTypes from 'prop-types';
import Card from 'zignal-landscape/lib/Card';
import Loading from 'zignal-landscape/lib/Loading';
import styles from './PresetIntentions.css';

/**
 * Preset Intentions mapped to actual dashboard template widget types
 * These widget types come from dashboard templates defined in /coffee/models/dashboard.coffee
 * See TEMPLATE_WIDGETS.md for complete template documentation
 *
 * Updated to match all dashboard templates from TEMPLATE_WIDGETS.md
 */
export const PRESET_INTENTIONS = {
  overview: {
    id: 'overview',
    title: 'Overview',
    icon: '📊',
    intention: 'I want a comprehensive overview of my workspace showing key metrics, trends, sentiment, geography, and content in one place.',
    description: 'Quick overview with key metrics and trends',
    widgetTypes: [
      'TotalMentions',
      'TotalMentionsChart',
      'MediaBreakdown',
      'TrendingStories',
      'RecentStories',
      'TopicOrEntityWheelChart',
      'PopularPosts',
      'SentimentPieChart',
      'HistoricalSentiment',
      'Atlas',
      'TopLocations',
      'TotalUniqueAuthors',
      'Word_Cloud',
      'TopAuthorsByEngagement',
      'InfluentialSitesByMentions'
    ]
  },
  discovery: {
    id: 'discovery',
    title: 'Discovery',
    icon: '🔍',
    intention: 'I want to discover trending topics, emerging hashtags, influential voices, and popular content to identify what is resonating with audiences.',
    description: 'Discover trending topics and emerging content',
    widgetTypes: [
      'TopLocations',
      'Word_Cloud',
      'TopMediaBarChart',
      'TotalMentions',
      'TopHashtagsBarChart',
      'TopHashtagWordCloud',
      'AverageCounts',
      'TopEmoji',
      'InfluentialSubRedditsByMentions',
      'InfluentialAuthorsByKlout',
      'TopAuthorsByEngagement',
      'InfluentialSitesByShares',
      'InfluentialSitesByMentions'
    ]
  },
  geographic: {
    id: 'geographic',
    title: 'Regional & Entity Monitoring',
    icon: '🌍',
    intention: 'I want to analyze geographic distribution of mentions, regional patterns, and entity relationships to understand location-based trends.',
    description: 'Track regional patterns and entity mentions',
    widgetTypes: [
      'TotalMentions',
      'Atlas',
      'TopLocations',
      'TopicOrEntityWheelChart',
      'EntityWordCloud'
    ]
  },
  // tags: {
  //   id: 'tags',
  //   title: 'Tags',
  //   icon: '🏷️',
  //   intention: 'I want to monitor tagged content and analyze how mentions are distributed across different tags and categories, including tag-specific sentiment.',
  //   description: 'Analyze mentions by tags and categories',
  //   widgetTypes: [
  //     'TotalMentionsChartbyIssue',
  //     'IssuesBarChartNew',
  //     'IssueSentimentBarChart'
  //   ]
  // },
  sentiment: {
    id: 'sentiment',
    title: 'Sentiment',
    icon: '😊',
    intention: 'I want to monitor sentiment trends and reputation changes, identifying potential issues early and tracking public perception patterns over time.',
    description: 'Track sentiment patterns and reputation',
    widgetTypes: [
      'SentimentPieChart',
      'HistoricalSentiment',
      'SentimentBreakdownByProfileChart',
      'Atlas',
      'IssueSentimentBarChart'
    ]
  },
  // advancedSentiment: {
  //   id: 'advancedSentiment',
  //   title: 'Advanced Sentiment',
  //   icon: '📈',
  //   intention: 'I want deep analysis of sentiment across multiple dimensions including volume, sources, profiles, authors, topics, and geographic distribution.',
  //   description: 'In-depth sentiment analysis across dimensions',
  //   widgetTypes: [
  //     'TotalMentionsChart',
  //     'TotalMentions',
  //     'HistoricalSentiment',
  //     'SentimentPieChart',
  //     'SentimentSmallGaugeChart',
  //     'MediaBreakdown',
  //     'AverageCounts',
  //     'RecentStories',
  //     'IssueSentimentBarChart',
  //     'InfluentialAuthorsByKlout',
  //     'TopAuthorsByEngagement',
  //     'TopHashtagWordCloud',
  //     'TopicOrEntityWheelChart',
  //     'Atlas'
  //   ]
  // },
  influencers: {
    id: 'influencers',
    title: 'Influencers & Audience',
    icon: '👥',
    intention: 'I want to identify and track influential authors, understand audience engagement, and analyze which sites and content drive the most impact.',
    description: 'Identify influential voices and engagement',
    widgetTypes: [
      'InfluentialAuthorsByMentions',
      'InfluentialAuthorsByKlout',
      'TopAuthorsByEngagement',
      'TotalUniqueAuthors',
      'InfluentialSitesByShares',
      'InfluentialSitesByMentions'
    ]
  }
};

const PresetIntentions = ({
  onSelectPreset,
  onCustomClick,
  hasExistingIntention = false,
  autoSelectedPresetId = null,
  similarityScore = null,
  savingPresetId = null
}) => {
  const handlePresetClick = (preset) => {
    if (savingPresetId) {
      return;
    }
    if (onSelectPreset) {
      onSelectPreset(preset);
    }
  };

  // disabled for now
  const handleCustom = () => {
    if (onCustomClick) {
      onCustomClick();
    }
  };

  const formatSimilarityScore = (score) => {
    if (!score) {
      return '';
    }
    return `${Math.round(score * 100)}%`;
  };

  return (
    <div className={styles.presetIntentions}>
      {autoSelectedPresetId && similarityScore && (
        <div className={styles.autoSelectNotice}>
          <span className={styles.autoSelectDot} />
          <span>
            Based on your dashboard widgets, we recommend the{' '}
            <strong>{PRESET_INTENTIONS[autoSelectedPresetId]?.title}</strong> preset
            ({formatSimilarityScore(similarityScore)} match).
            You can select a different preset if you prefer.
          </span>
        </div>
      )}
      <div className={styles.presetGrid}>
        {Object.values(PRESET_INTENTIONS).map(preset => {
          const isAutoSelected = autoSelectedPresetId === preset.id;
          const isSaving = savingPresetId === preset.id;
          const isDisabled = savingPresetId && !isSaving;
          const cardClassName = `${styles.presetCard} ${
            isAutoSelected ? styles.autoSelectedCard : ''
          } ${isSaving ? styles.savingCard : ''} ${isDisabled ? styles.disabledCard : ''}`;

          return (
            <div
              key={preset.id}
              className={cardClassName}
              onClick={() => handlePresetClick(preset)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onKeyDown={(e) => e.key === 'Enter' && handlePresetClick(preset)}
            >
              <Card>
                {isSaving && (
                  <div className={styles.savingOverlay}>
                    <Loading blockUi={false} width="2em" height="2em" />
                  </div>
                )}
                {isAutoSelected && !isSaving && (
                  <span className={styles.matchBadge}>
                    {formatSimilarityScore(similarityScore)} Match
                  </span>
                )}
                <div className={styles.presetHeader}>
                  <span className={styles.presetIcon}>{preset.icon}</span>
                  <h4 className={styles.presetTitle}>{preset.title}</h4>
                </div>
                <p className={styles.presetDescription}>{preset.description}</p>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
};

PresetIntentions.propTypes = {
  onSelectPreset: PropTypes.func.isRequired,
  onCustomClick: PropTypes.func.isRequired,
  hasExistingIntention: PropTypes.bool,
  autoSelectedPresetId: PropTypes.string,
  similarityScore: PropTypes.number,
  savingPresetId: PropTypes.string
};

export default PresetIntentions;
